import _debounce from 'lodash.debounce';
import moment from 'moment';

// Helpers
import getString from '@/helpers/GetStrings';

export default {
  data() {
    return {
      isLoading: false,
      tableHeight: 'calc(100vh - 280px)',
      css: {
        table: {
          tableClass: 'vuetable ui selectable celled stackable attached table vuetable-fixed-layout',
          loadingClass: '',
          ascendingIcon: 'mdi mdi-chevron-up icon',
          descendingIcon: 'mdi mdi-chevron-down icon',
          handleIcon: '',
        },
        pagination: {
          wrapperClass: 'pagination',
          activeClass: 'active',
          disabledClass: 'disabled',
          pageClass: '',
          linkClass: '',
          icons: {
            first: 'mdi mdi-chevron-double-left icon',
            prev: 'mdi mdi-chevron-left icon',
            next: 'mdi mdi-chevron-right icon',
            last: 'mdi mdi-chevron-double-right icon',
          },
        },
      },
      dateFormat: {
        'yyyy-MM-dd': 'YYYY-MM-DD',
        'dd-MM-yyyy': 'DD-MM-YYYY',
        'd-M-yyyy': 'D-M-YYYY',
        'dd-MM-yy': 'DD-MM-YY',
        'd-M-yy': 'D-M-YY',
      },
    };
  },
  methods: {
    noDataString(context) {
      if (this.isLoading) {
        return this.$store.state.stringsStore.language === 'nl' ? 'Bezig met laden...' : 'Loading...';
      }
      // ensure context is in lower case
      context = context.toLowerCase();
      const message = getString('list.nothingFound');
      const replacement = getString(`module.name.${context}`).toLowerCase();
      return message.replace('%records%', replacement);
    },
    // Format time (callback defined in columnsDefinition)
    tableFormatTime(value) {
      if (!value) {
        return '-';
      }
      // Unix timestamp formatted as "2018-16-05, 11:36 pm.."
      // return value > 0 ? moment(value).format('YYYY-DD-MM, h:mm a') : '-';
      return moment(value).format(`${this.dateFormat[this.$store.state.meStore.me.settings.date]}
        ${this.$store.state.meStore.me.settings.time}`);
    },
    fullname(name) {
      return `${name.firstname} ${name.surname}`;
    },

    time(timestamp) {
      return moment(timestamp).format('YYYY-DD-MM HH:MM:ss');
    },
    onRowClicked(row, event) {
      // Checks if table is selectable.
      // True for tables using the selectRows mixin.
      if (this.selectable) {
        this.selectRows(row, event);

      // Allow navigation to context-edit only when the user has rights.
      } else if (this.$store.state.meStore.me.rights.includes(`${this.permission}.edit`) || this.$store.state.meStore.me.su) {
        this.$router.push(`/${this.module}/${row.id}`);
      }
    },
    // Delay applying filters
    applyFilters: _debounce(function () {
      if (this.searchTerm !== '' && this.hideFilters !== false) {
        // Save the general filter to the store
        this.$store.dispatch('setFilters', {
          name: this.$route.name,
          filters: {
            general: this.searchTerm,
          },
        });
      } else {
        // Save the regular filters to the store
        this.$store.dispatch('setFilters', {
          name: this.$route.name,
          filters: this.filters,
        });
      }
      this.$nextTick(() => this.$refs.vuetable && this.$refs.vuetable.refresh());
    }, 100),
    showFilters() {
      // Clear search term when opening filters
      this.searchTerm = '';
      this.applyFilters();
      this.$nextTick(() => this.$refs.vuetable && this.$refs.vuetable.refresh());
      this.hideFilters = !this.hideFilters;
    },
    resetFilters() {
      Object.entries(this.filters).forEach((entry) => {
        const key = entry[0];
        const val = entry[1];
        if (Array.isArray(this.filters[key])) {
          this.filters[key] = [];
        } else {
          // Reset string filters to empty strings, and other types to null.
          this.filters[key] = typeof val === 'string' ? '' : null;
        }
      });
      this.$nextTick(() => this.$refs.vuetable && this.$refs.vuetable.refresh());
    },
    // Adjust the gutter column width based on the visible scrollbar
    applyScrollBarAdjust() {
      setTimeout(() => {
        const table = document.getElementsByClassName('vuetable');
        const bodyWrapper = table && table[0] && table[0].scrollWidth;
        const tableWrapper = table && table[1] && table[1].scrollWidth;
        const diff = bodyWrapper - tableWrapper;
        const gutter = document.getElementsByClassName('vuetable-gutter-col')[0];
        if (!gutter) {
          return;
        }
        // Specific to old IE
        if (document.all) {
          gutter.style.setAttribute('cssText', `width: ${diff}px !important;`);
          // Modern browser
        } else {
          gutter.setAttribute('style', `width: ${diff}px !important;`);
        }
      }, 100);
    },
    onLoading() {
      this.isLoading = true;
    },
    onSuccess(response) {
      // Set loading to false
      this.isLoading = false;
      // Modify the response object to fit vuetable.
      response.data.data = response.data[this.responseData.multiple];
      // For selectable tables, map row ids to an array.
      if (this.selectable) {
        this.rowData = response.data.data.map(row => row.id);
      }
    },
    // isActiveFilter is passed to the activeFilters computed property.
    // Here we check each filter type and return the expected value (true/false).
    isActiveFilter(filter) {
      let active = false;
      switch (typeof filter) {
        case 'string':
          // Empty strings return false.
          active = filter !== '';
          break;
        case 'object':
          // Null returns false. (indeterminate state on checkbox with 3 values).
          if (filter === null) {
            break;
          } else {
            // When the filter is an array, empty arrays return false. Empty objects return false.
            active = Array.isArray(filter) ? !!filter.length : filter !== {};
          }
          break;
        case 'boolean':
          // Both true and false values are considered active filters.
          active = true;
          break;
        default:
          break;
      }
      return active;
    },
  },
  mounted() {
    // Add column width for the scroll bar
    this.applyScrollBarAdjust();
  },
  updated() {
    // Add column width for the scroll bar
    this.applyScrollBarAdjust();
  },
  computed: {
    activeFilters() {
      // Returns true when there is one or more active filters
      return !!Object.values(this.filters).filter(this.isActiveFilter).length;
    },
  },
  filters: {
    capitalize(value) {
      if (!value) return '';
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  },
};
